import { Box } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import theme from '@fortress-technology-solutions/fortress-component-library/theme';
import { Slide } from '@fortress-technology-solutions/fortress-component-library/Transitions';
import { debounce } from '@fortress-technology-solutions/fortress-component-library/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useEventListener,
  useMutationObserver,
} from '@fortress-technology-solutions/fortress-component-library/hooks';

// Order the selectors by priority, from highest to lowest.
const SCROLL_CONTAINERS = ['.scroll-container', document];

const getActiveScrollContainer = () => {
  const scrollContainers = SCROLL_CONTAINERS.map((selector) => {
    const element =
      typeof selector === 'string'
        ? document.querySelector(selector)
        : selector;

    return element || null;
  });

  // first index should be the active element if the selector priority is accurate
  return scrollContainers.filter(Boolean)[0];
};

const AccessibleHomeIcon = (props) => {
  const documentRef = useRef(document.documentElement);
  const [activeContainer, setActiveContainer] = useState(null);
  const [showIcon, setShowIcon] = useState(true);

  const onScroll = useCallback(
    (event) => {
      const scrollTop =
        event.target.scrollTop ?? activeContainer?.documentElement?.scrollTop;

      // Only show the icon if user is at the top of the screen
      if (scrollTop > 0) {
        setShowIcon(false);
      } else {
        setShowIcon(true);
      }
    },
    [activeContainer],
  );

  // debounce pattern triggers warning
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetActiveContainer = useCallback(
    debounce(() => {
      const container = getActiveScrollContainer();
      if (container) {
        setActiveContainer(container);
      }
    }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      if (debouncedSetActiveContainer?.clear) {
        debouncedSetActiveContainer.clear();
      }
    };
  }, [debouncedSetActiveContainer]);

  // The mutation observer is used to detect when the scroll container is added to the DOM.
  useMutationObserver(documentRef, debouncedSetActiveContainer, {
    childList: true,
    subtree: true,
  });

  // The event listener is used to detect when the user scrolls within the active container.
  useEventListener('scroll', activeContainer, onScroll);

  return (
    <Slide direction="up" in={showIcon} timeout={500}>
      <Box
        id={'accessible-home-icon'}
        component={'svg'}
        viewBox="0 0 160 75"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        sx={{
          position: 'fixed',
          height: { xs: 45, sm: 55 },
          bottom: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          zIndex: theme.zIndex.modal - 1,
        }}
        {...props}
      >
        {/* Background rounded rectangle */}
        <rect
          x="0.5"
          y="0.5"
          width="160"
          height="73"
          rx="36.5"
          fill={theme.palette.common.white}
          stroke={theme.palette.grey[300]}
          strokeWidth="1"
        />
        {/* Divider */}
        <rect x="80.5" y="12" width="2" height="50" rx="1" fill="#BDBDBD" />
        {/* Wheelchair icon */}
        <g transform="translate(20, 10) scale(1.2)">
          <path
            d="M15.0859 5.4585C17.8204 5.4585 20.0897 7.72798 20.0898 10.4624C20.0897 12.645 18.7583 14.5537 16.7012 15.2163L17.4824 21.0532L23.793 19.4517V19.4526C24.1213 19.3538 24.4189 19.4116 24.6602 19.5532C24.8854 19.6856 25.0474 19.8812 25.1533 20.022L25.209 20.0962L25.2344 20.186C25.3422 20.5641 25.2609 20.8972 25.0811 21.1519C24.9169 21.384 24.6902 21.5269 24.5264 21.6089L24.4814 21.6313L24.4326 21.644L17.8018 23.4331L18.4121 27.9888C19.6717 27.7707 21.7086 27.402 23.6445 27.064C23.6881 27.0564 23.7319 27.0481 23.7754 27.0405C23.8059 26.9994 23.8395 26.9598 23.877 26.9224C24.1158 26.6839 24.4354 26.5913 24.7549 26.5913C24.9761 26.5914 25.194 26.647 25.3818 26.7661C25.8513 26.6876 26.2939 26.6157 26.6924 26.5542C27.538 26.4236 28.2354 26.3307 28.5566 26.3306C29.4964 26.3308 30.0677 27.0915 30.4639 27.9907C30.7709 28.688 31.0346 29.606 31.2959 30.6841L31.5586 31.814L31.5605 31.8257C31.6589 32.3171 31.8192 32.9521 31.9531 33.5034C32.1247 33.4553 32.3108 33.4013 32.499 33.3452C32.8665 33.2356 33.25 33.1185 33.5215 33.0503C33.8636 32.9553 34.1897 32.9959 34.46 33.1704C34.7142 33.335 34.8644 33.5833 34.9512 33.8003L34.957 33.814L34.9609 33.8276C35.0739 34.1665 35.0194 34.4823 34.8623 34.7339C34.7174 34.9656 34.5041 35.1166 34.3242 35.2065L34.123 35.3071L33.959 35.2407C33.9537 35.2408 33.9468 35.2404 33.9395 35.2407C33.8882 35.243 33.8126 35.2524 33.7119 35.269C33.5101 35.3024 33.255 35.3593 32.9707 35.4243C32.6929 35.4879 32.3888 35.5585 32.1113 35.6128C32.0179 35.6311 31.9219 35.6458 31.8281 35.6606V35.7124H31.3281C31.0265 35.7123 30.7415 35.6328 30.4961 35.4302C30.2668 35.2406 30.114 34.9771 29.9961 34.6958C29.7674 34.1499 29.5772 33.3005 29.2959 32.1743L29.2939 32.1655L29.292 32.1558C29.163 31.5428 28.9719 30.6538 28.7588 29.8726C28.652 29.481 28.5431 29.1271 28.4365 28.854C28.3911 28.7376 28.3469 28.6428 28.3086 28.5688C28.0046 28.5941 27.5396 28.6578 26.9414 28.7534C26.6231 28.8043 26.2735 28.8647 25.8994 28.9302V29.8628C25.8994 34.5345 21.9878 38.2911 17.2783 38.2915H16.7783V38.272C12.4696 38.0069 8.97852 34.3433 8.97852 29.8618C8.97875 26.045 11.8062 22.5588 15.2568 21.6157L14.4629 15.4243C12.0156 15.111 10.0822 12.9858 10.082 10.4624C10.0822 7.72815 12.3517 5.45878 15.0859 5.4585ZM15.5615 23.9966C14.6356 24.3085 13.6763 24.9547 12.9014 25.8481C11.967 26.9257 11.3312 28.3278 11.3311 29.8628C11.3311 33.1382 14.1436 35.9366 17.2764 35.937C20.6252 35.9369 23.5449 33.1155 23.5449 29.8628V29.3618C21.6036 29.7293 19.3831 30.1665 17.6904 30.4819L17.6455 30.4907H17.5996C17.5096 30.4907 17.3575 30.492 17.209 30.4692C17.0686 30.4476 16.8424 30.3927 16.665 30.2153L16.6299 30.1802L16.6025 30.1392C16.5772 30.101 16.5503 30.0637 16.5205 30.0229C16.4931 29.9854 16.4563 29.9364 16.4248 29.8892C16.3757 29.8154 16.2825 29.6669 16.2646 29.4741L15.5615 23.9966ZM15.0869 7.56104C13.4751 7.56104 12.1855 8.91553 12.1855 10.4624C12.1855 11.9453 13.604 13.3638 15.0869 13.3638C16.6336 13.3635 17.9873 11.9452 17.9873 10.4624C17.9873 8.91567 16.7625 7.56128 15.0869 7.56104Z"
            fill="black"
          />
        </g>
        {/* House icon */}
        <g transform="translate(137, 60) scale(0.0052) rotate(180 614 654)">
          <path
            d="m 3025.14,3676.04 c 0,436.75 0,871.1 0,1307.77 1058.45,0 2115.98,0 3175.85,0 0,-434.81 0,-870.14 0,-1307.77 -1057.05,0 -2114.58,0 -3175.85,0"
            fill="#100f0d"
          />
          <path
            d="m 6189.99,6500.05 c 0,-436.78 0,-871.57 0,-1308.25 -1054.87,0 -2112.23,0 -3171.75,0 0,435.49 0,870.73 0,1308.25 1054.41,0 2109.05,0 3171.75,0"
            fill="#100f0d"
          />
          <path
            d="m 7621.68,3127.31 c 0,-6.59 0,-13.17 0,-23.26 -2007,0 -4009.11,0 -6013.96,0 -0.65,12.47 -1.79,24.18 -1.79,35.9 -0.09,1013.44 0,2026.89 -0.5,3040.33 -0.01,26.73 7.92,41.51 31.78,57.33 286.9,190.37 572.45,382.76 858.4,574.55 198.63,133.24 397.35,266.34 596.02,399.54 198.67,133.2 397.53,266.12 595.91,399.76 203.97,137.4 407.41,275.59 611.19,413.27 90.37,61.06 181.48,121.03 271.19,183.05 20.89,14.45 35.49,12.8 55.59,-0.73 230.08,-154.85 460.55,-309.09 691.22,-463.06 510.37,-340.69 1020.98,-681.04 1531.37,-1021.7 249.19,-166.34 498.24,-332.9 746.96,-499.96 15.23,-10.24 27.71,-21.18 27.68,-46.52 -1.24,-1016.16 -1.06,-2032.33 -1.06,-3048.5 z M 9211.23,6730.55 4666.66,9803.56 c -33.33,22.53 -87.76,23.58 -120.05,2 -310.18,-207.31 -620.53,-414.36 -930.48,-622.01 -214.02,-143.39 -427.37,-287.8 -641.24,-431.4 -214.88,-144.29 -430.1,-288.06 -645.07,-432.22 -222.65,-149.31 -445.11,-298.9 -667.76,-448.22 C 1447.09,7727.55 1231.93,7583.69 1016.97,7439.53 794.316,7290.22 571.711,7140.84 349.262,6991.23 232.641,6912.78 116.406,6833.76 0,6755.01 0,6341.99 0,5928.99 0,5515.97 c 199.52,0 399.043,0 601.797,0 0,-1217 0,-2432.2 0,-3653.18 29.965,-2.44 57.187,-6.57 84.41,-6.58 960.493,-0.33 1920.983,0.66 2881.473,-0.49 1670.35,-1.97 3340.7,-5.22 5011.05,-7.93 10.76,-0.02 21.52,0 35.49,0 0,1219.14 0,2436.99 0,3660.03 201.13,0 399.07,0 597.01,0 0,407.58 0,815.16 0,1222.73 -15.96,-28.25 -27.02,-7.71 0,0"
            fill="#100f0d"
          />
        </g>
      </Box>
    </Slide>
  );
};

export default AccessibleHomeIcon;
